import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiEndpoint } from 'app/agriculture/queries/apiEndpoints';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { use } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import {
    Farm,
    farmSchema,
} from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/farmSchema';
import { PointGeometry } from 'app/agriculture/queries/types/geometry/PointGeometry';

interface NewFarm {
    name: string;
    timeZone: string;
    center: PointGeometry;
}

export function useFarmCreateMutation() {
    const { agricultureToastService } = use(ServicesContext);
    const instance = createAxiosInstance();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async function (farm: NewFarm) {
            const { data } = await instance.post<unknown>(ApiEndpoint.farms, {
                ...farm,
                agSetupComplete: true,
            });

            return farmSchema.parse(data) as Farm;
        },
        onSuccess: async function (farm: Farm) {
            // add new farm to farms query cache
            queryClient.setQueryData<Farm[]>([ApiEndpoint.farms], (cachedFarms) => [
                ...(cachedFarms ?? []),
                farm,
            ]);

            // add new farm to single farm query cache
            queryClient.setQueryData([`${ApiEndpoint.farms}${farm.id}`], farm);

            await agricultureToastService.showToast({
                title: 'Farm created',
                type: AgricultureToastType.SUCCESS,
                variant: 'simple',
            });
        },
        onError: async function (error) {
            console.error('Error creating farm', error);
            await agricultureToastService.showToast({
                title: 'Farm Creation Failed',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });
        },
    });
}
